import React from 'react';

import {
  Table,
  cellFactory,
  TableCellPropsUnion,
  TableSelectableCellProps,
  ORDERING,
} from '@components/Table/Table';
import {
  adminBookingInquiryTradeConditionOptions,
  bookingInquiryStatusOptions,
} from '@data/adminBookingInquiry';
import { getInstructionStatusLabel, instructionStrings } from '@data/instruction';
import type { Agency } from '@models/Agency';
import type { InquiryStatus } from '@models/BookingInquiry';
import { InstructionStatusName, instructionStatusName } from '@models/Instruction';
import { BookingInquiryStatus } from '@models/next/BookingInquiryEnums';
import { allTransportStatus } from '@models/TrackingData';
import { makeMuiStyles } from '@styles';
import { createStrings } from '@utils/local';
import { Link } from 'react-router-dom';

export { ORDERING };

const useStyles = makeMuiStyles((theme, common) => ({
  link: {
    ...common.whitespacePre,
    color: theme.palette.primary.main,
  },
}));

const strings = createStrings({
  ja: {
    details: '詳細',
  },
});

const tableHeadStrings = createStrings({
  ja: {
    id: 'NO.',
    d2dNo: 'D2D番号',
    receptionHours: '受付時間',
    membershipNumber: '会員番号',
    companyName: '会社名',
    companyEnglishName: 'OWNER',
    vesselName: '船名',
    tradeCondition: '貿易条件',
    status: 'ステータス',
    responseStatus: '対応状況',
    responseResults: '対応結果',
    csManagerChina: 'CS担当者（中国）',
    csManagerJapan: 'CS担当者（日本）',
    memberSalesperson: '営業担当者',
    eta: 'ETA',
    etd: 'ETD',
    pod: 'POD',
    myCases: '私の案件',
    consignee: 'CNEE',
    applicant: '申込者',
    agency: '代理店',
  },
});

export const typeStrings = createStrings({
  ja: {
    p2d: 'P2D',
    d2d: 'D2D',
  },
});

export const TYPE = Object.freeze({
  P2D: 'P2D',
  D2D: 'D2D',
} as const);

type Functional<T, A extends Array<any> = []> = (...args: A) => T;

export interface HeadColumns {
  /**
   * NO.
   */
  id: Functional<TableCellPropsUnion>;
  /**
   * D2D番号
   */
  d2dNumber: Functional<TableCellPropsUnion>;
  /**
   * 受付時間
   */
  receptionHours: Functional<TableCellPropsUnion, [TableSelectableCellProps['onChange']]>;
  /**
   * 会員番号
   */
  membershipNumber: Functional<TableCellPropsUnion>;
  /**
   * Owner
   */
  companyEnglishName: Functional<TableCellPropsUnion>;
  /**
   * 申込者
   */
  applicant: Functional<TableCellPropsUnion>;
  /**
  /**
   * 会社名
   */
  companyName: Functional<TableCellPropsUnion>;
  /**
   * 船名
   */
  vesselName: Functional<TableCellPropsUnion>;
  /**
   * 貿易条件
   */
  tradeCondition: Functional<TableCellPropsUnion, [TableSelectableCellProps['onChange']]>;
  /**
   * ステータス
   */
  status: Functional<TableCellPropsUnion, [TableSelectableCellProps['onChange']]>;
  /**
   * 通関書類
   */
  customsInstruction: Functional<TableCellPropsUnion, [TableSelectableCellProps['onChange']]>;
  /**
   * 輸送状況 for current
   */
  currentTransportStatus: Functional<TableCellPropsUnion, [TableSelectableCellProps['onChange']]>;
  /**
   * 輸送状況 for past
   */
  pastTransportStatus: Functional<TableCellPropsUnion, [TableSelectableCellProps['onChange']]>;
  /**
   * 営業担当者
   */
  memberSalesperson: Functional<TableCellPropsUnion, [TableSelectableCellProps['onChange']]>;
  /**
   * 対応状況
   */
  responseStatus: Functional<TableCellPropsUnion>;
  /**
   * 対応結果
   */
  responseResults: Functional<TableCellPropsUnion>;
  /**
   * CS担当者
   */
  csManagerChina: Functional<TableCellPropsUnion, [TableSelectableCellProps['onChange']]>;
  csManagerJapan: Functional<TableCellPropsUnion, [TableSelectableCellProps['onChange']]>;
  etd: Functional<TableCellPropsUnion, [TableSelectableCellProps['onChange']]>;
  eta: Functional<TableCellPropsUnion, [TableSelectableCellProps['onChange']]>;
  /**
   * 配車書類
   */
  deliveryInstruction: Functional<TableCellPropsUnion, [TableSelectableCellProps['onChange']]>;
  pod: Functional<TableCellPropsUnion, [TableSelectableCellProps['onChange']]>;
  blank: Functional<TableCellPropsUnion>;
  consignee: Functional<TableCellPropsUnion>;
  agency: Functional<
    TableCellPropsUnion,
    [TableSelectableCellProps['onChange'], TableSelectableCellProps['options']]
  >;
}

/* eslint-disable max-len */
export const headColumns: HeadColumns = Object.freeze({
  id: () => cellFactory.create(tableHeadStrings.id),
  d2dNumber: () => cellFactory.create(tableHeadStrings.d2dNo),
  receptionHours: (onChange: any) =>
    cellFactory.createOrdering(tableHeadStrings.receptionHours, onChange),
  membershipNumber: () => cellFactory.create(tableHeadStrings.membershipNumber),
  companyName: () => cellFactory.create(tableHeadStrings.companyName),
  companyEnglishName: () => cellFactory.create(tableHeadStrings.companyEnglishName),
  applicant: () => cellFactory.create(tableHeadStrings.applicant),
  vesselName: () => cellFactory.create(tableHeadStrings.vesselName),
  tradeCondition: (onChange: any) =>
    cellFactory.createSelectable(
      tableHeadStrings.tradeCondition,
      adminBookingInquiryTradeConditionOptions,
      onChange,
    ),
  status: (onChange: any) =>
    cellFactory.createSelectable(tableHeadStrings.status, bookingInquiryStatusOptions, onChange),
  customsInstruction: (onChange: any) =>
    cellFactory.createSelectable(
      instructionStrings.customsFiles,
      [
        ...instructionStatusName.types.map((t) => ({
          label: getInstructionStatusLabel(t.value, true),
          value: t.value,
        })),
        {
          label: 'ALL',
          value: undefined,
        },
      ],
      onChange,
    ),
  currentTransportStatus: (onChange: any) =>
    cellFactory.createSelectable(
      tableHeadStrings.status,
      [
        ...allTransportStatus
          .filter(
            (item) =>
              !['キャンセル済', '支払済', '通関と納品の最終指示', 'RCEP依頼済'].includes(item),
          )
          .map((value) => ({
            label: value,
            value,
          })),
        { label: 'ALL', value: '' },
      ],
      onChange,
    ),
  pastTransportStatus: (onChange: any) =>
    cellFactory.createSelectable(
      tableHeadStrings.status,
      [
        ...['FCキャンセル済', '客側キャンセル済', '支払済'].map((value) => ({
          label: value,
          value,
        })),
        { label: 'ALL', value: '' },
      ],
      onChange,
    ),
  responseStatus: () => cellFactory.create(tableHeadStrings.responseStatus),
  responseResults: () => cellFactory.create(tableHeadStrings.responseResults),
  memberSalesperson: (onChange: any) =>
    cellFactory.createSelectable(
      tableHeadStrings.memberSalesperson,
      [
        { label: tableHeadStrings.myCases, value: true },
        { label: 'ALL', value: false },
      ],
      onChange,
    ),
  csManagerChina: (onChange: any) =>
    cellFactory.createSelectable(
      tableHeadStrings.csManagerChina,
      [
        { label: tableHeadStrings.myCases, value: true },
        { label: 'ALL', value: false },
      ],
      onChange,
    ),
  csManagerJapan: (onChange: any) =>
    cellFactory.createSelectable(
      tableHeadStrings.csManagerJapan,
      [
        { label: tableHeadStrings.myCases, value: true },
        { label: 'ALL', value: false },
      ],
      onChange,
    ),
  eta: (onChange: any) => cellFactory.createOrdering(tableHeadStrings.eta, onChange),
  etd: (onChange: any) => cellFactory.createOrdering(tableHeadStrings.etd, onChange),
  deliveryInstruction: (onChange: any) =>
    cellFactory.createSelectable(
      instructionStrings.deliveryFiles,
      [
        ...instructionStatusName.types.map((t) => ({
          label: getInstructionStatusLabel(t.value, true),
          value: t.value,
        })),
        {
          label: 'ALL',
          value: undefined,
        },
      ],
      onChange,
    ),
  pod: (onChange: any) => cellFactory.createDictOrdering(tableHeadStrings.pod, onChange),
  blank: () => cellFactory.createBlank(),
  consignee: () => cellFactory.create(tableHeadStrings.consignee),
  agency: (onChange: any, options: { value: any; label: any }[]) =>
    cellFactory.createSelectable(
      tableHeadStrings.agency,
      [...options, { label: 'ALL', value: undefined }],
      onChange,
    ),
});
/* eslint-enable max-len */

/**
 * The table of 予約一覧
 */
export const BookingInquiryTable: React.FC<BookingInquiryTableProps> = (props) => {
  const {
    initialIndex = 1,
    items,
    agencyList,
    onReceptionHoursChange,
    onStatusChange,
    onMemberSalespersonChange,
    onCsManagerJapanChange,
    onCsManagerChinaChange,
    onAgencyIdChange,
    detailsURL,
  } = props;
  const classes = useStyles();

  return (
    <Table columnFixedUntil={3} fixedWidths={[60, 120, 60]}>
      <Table.Head>
        <Table.Row
          columns={[
            headColumns.id(),
            headColumns.d2dNumber(),
            headColumns.blank(),
            headColumns.receptionHours(
              onReceptionHoursChange as TableSelectableCellProps['onChange'],
            ),
            headColumns.membershipNumber(),
            headColumns.companyEnglishName(),
            headColumns.applicant(),
            headColumns.consignee(),
            headColumns.agency(
              (value) => onAgencyIdChange(value as number),
              agencyList.map((a) => ({ label: a.name_en_short, value: a.id })),
            ),
            headColumns.status((value) => onStatusChange(value as InquiryStatus)),
            headColumns.memberSalesperson((value) => onMemberSalespersonChange(value as boolean)),
            headColumns.csManagerChina((value) => onCsManagerChinaChange(value as boolean)),
            headColumns.csManagerJapan((value) => onCsManagerJapanChange(value as boolean)),
          ]}
        />
      </Table.Head>
      <Table.Body>
        {items.map((item, i) => (
          <Table.Row
            key={item.id}
            columns={[
              cellFactory.create(i + initialIndex),
              cellFactory.create(item.d2dNumber),
              cellFactory.create(
                <Link className={classes.link} to={`${detailsURL}?d2dNo=${item.id}`}>
                  {strings.details}
                </Link>,
              ),
              cellFactory.create(item.receptionHours),
              cellFactory.create(item.membershipNumber),
              cellFactory.create(item.englishName),
              cellFactory.create(item.applicant),
              cellFactory.create(item.consignee),
              cellFactory.create(item.agencyName),
              cellFactory.create(item.status),
              cellFactory.create(item.memberSalesperson),
              cellFactory.create(item.csManagerChina),
              cellFactory.create(item.csManagerJapan),
            ]}
          />
        ))}
      </Table.Body>
    </Table>
  );
};

export interface BookingInquiryItem {
  id: string;
  d2dNumber: React.ReactNode;
  receptionHours: React.ReactNode;
  membershipNumber: React.ReactNode;
  englishName: React.ReactNode;
  applicant: React.ReactNode;
  consignee: React.ReactNode;
  status: React.ReactNode;
  memberSalesperson: React.ReactNode;
  csManagerChina: React.ReactNode;
  csManagerJapan: React.ReactNode;
  agencyName: React.ReactNode;
}

export interface BookingInquiryTableProps {
  initialIndex: number;
  items: BookingInquiryItem[];
  agencyList: Agency[];
  onStatusChange: (value: BookingInquiryStatus) => void;
  onReceptionHoursChange: (value: keyof typeof ORDERING) => void;
  onMemberSalespersonChange: (isMine: boolean) => void;
  onCsManagerChinaChange: (isMine: boolean) => void;
  onCsManagerJapanChange: (isMine: boolean) => void;
  onAgencyIdChange: (value: number) => void;
  detailsURL: string;
}

export interface BookingItem {
  id: string;
  d2dNumber: React.ReactNode;
  createdAt: React.ReactNode;
  englishName: React.ReactNode;
  applicant: React.ReactNode;
  consignee: React.ReactNode;
  tradeCondition: React.ReactNode;
  status: React.ReactNode;
  customsInstructionStatus: React.ReactNode;
  memberSalesperson: React.ReactNode;
  csManagerChina: React.ReactNode;
  csManagerJapan: React.ReactNode;
  etd: React.ReactNode;
  eta: React.ReactNode;
  deliveryInstructionStatus: React.ReactNode;
  vesselName: React.ReactNode;
  pod: React.ReactNode;
  agencyName: React.ReactNode;
}

export interface BookingTableProps {
  type: 'current' | 'past_canceled' | 'past_finished';
  initialIndex: number;
  items: BookingItem[];
  onReceptionHoursChange: TableSelectableCellProps['onChange'];
  onTradeCondtionChange: (value: string) => void;
  detailsURL: string;
  checklist: BookingItem['id'][];
  agencyList: Agency[];
  onChecklistChange: (nextCheckList: BookingTableProps['checklist']) => void;
  onTransportStatusChange: (value: string | number | boolean) => void;
  onCustomsInstructionStatusChange: (value: InstructionStatusName | undefined) => void;
  onDeliveryInstructionStatusChange: (value: InstructionStatusName | undefined) => void;
  onMemberSalespersonChange: (isMine: boolean) => void;
  onCsManagerChinaChange: (isMine: boolean) => void;
  onCsManagerJapanChange: (isMine: boolean) => void;
  onEtaOrderChange: (value: string | number | boolean) => void;
  onEtdOrderChange: (value: string | number | boolean) => void;
  onPodOrderChange: (value: string | number | boolean) => void;
  onAgencyIdChange: (value: number) => void;
}

/**
 * The table of {現在,過去}の予約
 */
export const BookingTable: React.FC<BookingTableProps> = (props) => {
  const {
    type,
    initialIndex,
    items,
    agencyList,
    detailsURL,
    checklist,
    onChecklistChange,
    onTransportStatusChange,
    onCustomsInstructionStatusChange,
    onDeliveryInstructionStatusChange,
    onMemberSalespersonChange,
    onCsManagerChinaChange,
    onCsManagerJapanChange,
    onEtaOrderChange,
    onEtdOrderChange,
    onPodOrderChange,
    onTradeCondtionChange,
    onReceptionHoursChange,
    onAgencyIdChange,
  } = props;
  const classes = useStyles();

  const allChecked = items.length > 0 && items.every((item) => checklist.includes(item.id));
  const currentItemIds = items.map((item) => item.id);
  const onHeadCheckableChange = () => {
    if (allChecked) {
      onChecklistChange(checklist.filter((id) => !currentItemIds.includes(id)));
    } else {
      onChecklistChange([...checklist, ...currentItemIds]);
    }
  };
  const isChecked = (item: BookingItem): boolean => {
    return checklist.includes(item.id);
  };
  const onBodyCheckableChange = (item: BookingItem) => () => {
    if (isChecked(item)) {
      onChecklistChange(checklist.filter((id) => id !== item.id));
    } else {
      onChecklistChange([...checklist, item.id]);
    }
  };

  return (
    <Table columnFixedUntil={4} fixedWidths={[50, 50, 100, 60]}>
      <Table.Head>
        <Table.Row
          columns={[
            cellFactory.createCheckable(allChecked, onHeadCheckableChange),
            headColumns.id(),
            headColumns.d2dNumber(),
            headColumns.blank(),
            headColumns.receptionHours(onReceptionHoursChange),
            headColumns.companyEnglishName(),
            headColumns.applicant(),
            headColumns.consignee(),
            headColumns.agency(
              (value) => onAgencyIdChange(value as number),
              agencyList.map((a) => ({ value: a.id, label: a.name_en_short })),
            ),
            headColumns.tradeCondition((value) => onTradeCondtionChange(value as string)),
            type === 'current'
              ? headColumns.currentTransportStatus((value) => onTransportStatusChange(value as any))
              : headColumns.pastTransportStatus((value) => onTransportStatusChange(value as any)),
            headColumns.customsInstruction((value) =>
              onCustomsInstructionStatusChange(value as any),
            ),
            headColumns.deliveryInstruction((value) =>
              onDeliveryInstructionStatusChange(value as any),
            ),
            headColumns.memberSalesperson((value) => onMemberSalespersonChange(value as boolean)),
            headColumns.etd((value) => onEtdOrderChange(value as any)),
            headColumns.eta((value) => onEtaOrderChange(value as any)),
            headColumns.vesselName(),
            headColumns.pod((value) => onPodOrderChange(value as any)),
            headColumns.csManagerChina((value) => onCsManagerChinaChange(value as boolean)),
            headColumns.csManagerJapan((value) => onCsManagerJapanChange(value as boolean)),
          ]}
        />
      </Table.Head>
      <Table.Body>
        {items.map((item, i) => (
          <Table.Row
            key={item.id}
            columns={[
              cellFactory.createCheckable(isChecked(item), onBodyCheckableChange(item)),
              cellFactory.create(i + initialIndex),
              cellFactory.create(item.d2dNumber),
              cellFactory.create(
                <Link className={classes.link} to={`${detailsURL}?d2dNo=${item.id}&type=${type}`}>
                  {strings.details}
                </Link>,
              ),
              cellFactory.create(item.createdAt),
              cellFactory.create(item.englishName),
              cellFactory.create(item.applicant),
              cellFactory.create(item.consignee),
              cellFactory.create(item.agencyName),
              cellFactory.create(item.tradeCondition),
              cellFactory.create(item.status),
              cellFactory.create(item.customsInstructionStatus),
              cellFactory.create(item.deliveryInstructionStatus),
              cellFactory.create(item.memberSalesperson),
              cellFactory.create(item.etd),
              cellFactory.create(item.eta),
              cellFactory.create(item.vesselName),
              cellFactory.create(item.pod),
              cellFactory.create(item.csManagerChina),
              cellFactory.create(item.csManagerJapan),
            ]}
          />
        ))}
      </Table.Body>
    </Table>
  );
};
